import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, PackageSearch } from "lucide-react";
import { myOrdersQuery } from "@/lib/orders.functions";
import { useAuth } from "@/lib/auth";
import { formatBRL } from "@/lib/format";

export const Route = createFileRoute("/minha-conta/pedidos")({
  head: () => ({
    meta: [{ title: "Meus Pedidos — Loja EG Elétrica" }, { name: "robots", content: "noindex" }],
  }),
  component: MeusPedidosPage,
});

const STATUS_LABEL: Record<string, string> = {
  pending: "Aguardando pagamento",
  paid: "Pagamento confirmado",
  processing: "Em processamento",
  shipped: "Enviado",
  delivered: "Entregue",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};

function MeusPedidosPage() {
  const user = useAuth((s) => s.user);
  const initialized = useAuth((s) => s.initialized);

  const { data: orders, isLoading } = useQuery({
    ...myOrdersQuery(),
    enabled: !!user,
  });

  if (initialized && !user) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">Meus Pedidos</h1>
        <p className="text-sm text-muted-foreground mb-4">
          Entre na sua conta para ver seus pedidos.
        </p>
        <Link to="/login" className="text-sm font-medium text-eg-goldtext hover:underline">
          Entrar
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Meus Pedidos</h1>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
        </div>
      )}

      {!isLoading && orders?.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <PackageSearch className="mx-auto mb-3 h-10 w-10" />
          Você ainda não fez nenhum pedido.
        </div>
      )}

      <div className="space-y-3">
        {orders?.map((order) => (
          <Link
            key={order.id}
            to="/pedido/$id"
            params={{ id: order.id }}
            className="block rounded-lg border border-border bg-white p-4 transition-colors hover:border-eg-gold"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-eg-dark">Pedido #{order.order_number}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(order.created_at).toLocaleDateString("pt-BR")} ·{" "}
                  {STATUS_LABEL[order.status] ?? order.status}
                </p>
              </div>
              <p className="font-semibold text-eg-goldtext">{formatBRL(order.total)}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
